/**
 * Storefront CMS content — the buyer storefront's marketing chrome (hero slides,
 * promo + campaign banners, vouchers, collections, store-stat blocks, trust /
 * assurance badges, header utility links + search suggestions, footer columns +
 * brand copy, and a few inline copy strings). Previously hardcoded in
 * `apps/member/src/storefront/lib/mock.ts` and inline in the page components;
 * now admin-editable and served via `GET /storefront/content`.
 *
 * Stored as ONE `storefront` key on `tenant_config.features` (jsonb) — same
 * pattern as `flash_sale` / `ecom_payment_methods`, no dedicated table. Because
 * `patchFeatures` shallow-merges top-level keys, the admin editor sends the FULL
 * `storefront` block on every save (its draft holds the whole resolved object).
 * `resolveStorefront` deep-merges whatever is stored against `STOREFRONT_DEFAULT`
 * so a blob written by older code (missing newer sub-blocks) still resolves to a
 * complete, correctly-typed config — see the unit test.
 *
 * Conventions:
 *  - Images are stored as R2 object KEYS only (`imageKey`), never presigned URLs.
 *    Served through the storefront asset route by stable id.
 *  - Icons are a closed `StorefrontIconKey` allow-list mapped to lucide on the
 *    client (never a component reference in JSON).
 *  - Editing is through typed row editors in the admin Storefront page — NO raw
 *    JSON.
 */

import { LAYOUT_DEFAULT_OMNI, resolveLayout, type StorefrontLayout } from './storefront-sections';

/** Closed allow-list of icons the storefront can render (mapped to lucide client-side). */
export type StorefrontIconKey =
  | 'shield'
  | 'rotate'
  | 'truck'
  | 'banknote'
  | 'coins'
  | 'lock'
  | 'box'
  | 'star'
  | 'users'
  | 'chat'
  | 'user'
  | 'clock';

export const STOREFRONT_ICON_KEYS: StorefrontIconKey[] = [
  'shield',
  'rotate',
  'truck',
  'banknote',
  'coins',
  'lock',
  'box',
  'star',
  'users',
  'chat',
  'user',
  'clock',
];

export type HeroSlide = {
  kicker: string;
  title: string;
  highlight: string;
  date: string;
  /** Optional R2 object key for a slide background image. */
  imageKey?: string;
};

export type PromoBanner = {
  title: string;
  sub: string;
  /** CSS gradient used when no `imageKey` is set. */
  gradient?: string;
  imageKey?: string;
};

export type CampaignBanner = {
  title: string;
  sub: string;
  ctaLabel: string;
  ctaHref: string;
  gradient?: string;
  imageKey?: string;
};

export type StorefrontCollection = {
  name: string;
  /** When linked to a category, the storefront can show a live item count. */
  categoryId?: string;
  /** Static fallback label (e.g. "84 items") when not category-linked. */
  items?: string;
  imageKey?: string;
};

export type StorefrontVoucher = {
  /** Large value, e.g. "₱100" or "15%". */
  big: string;
  /** Small qualifier, e.g. "OFF". */
  sm: string;
  /** Terms line, e.g. "Min. spend ₱1,000". */
  terms: string;
  /** Validity line, e.g. "Valid till 06.30". */
  validity: string;
};

export type IconBadge = {
  label: string;
  icon: StorefrontIconKey;
};

export type ProductVoucherBadge = {
  line1: string;
  line2: string;
};

/** A stat with a manual value, or a live-computed one (`live` overrides `value`). */
export type StorefrontStat = {
  value: string;
  label: string;
  /** When set, the storefront replaces `value` with a computed figure. */
  live?: 'productCount';
};

export type IconStat = StorefrontStat & {
  icon: StorefrontIconKey;
};

export type StorefrontLink = {
  label: string;
  href: string;
};

export type FooterColumn = {
  heading: string;
  links: StorefrontLink[];
};

/**
 * Routes that only exist while the storefront + member portal are mounted.
 *
 * In `public_site_info_only` mode the member SPA drops these routes entirely, so
 * any CMS-authored header/footer link pointing at one would dead-end on the
 * catch-all redirect. We filter at RENDER rather than rewriting the stored
 * defaults — live tenant databases already hold their own copies of these links,
 * and a data migration would clobber admin-customized content.
 */
const COMMERCE_ONLY_PATHS = [
  '/login',
  '/signup',
  '/cart',
  '/checkout',
  '/shop',
  '/search',
  '/track',
  '/orders',
  '/store',
  '/official',
  '/product',
  '/storefront',
  '/sell-on-omni',
  '/become-member',
  '/become-merchant',
] as const;

/** True when `href` points at a route that disappears in info-only mode. */
export function isCommercePath(href: string): boolean {
  if (!href.startsWith('/')) return false; // external / mailto / anchor — leave alone
  const path = href.split(/[?#]/)[0]!.replace(/\/+$/, '') || '/';
  return COMMERCE_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * A storefront informational page (About Us, Become a Member, Sell on Omni,
 * Become a Merchant guide). An ordered list of sections covers the spec —
 * paragraph blocks, ordered "how-to" step lists, and bullet doc lists — without
 * a full rich-text editor. Edited via typed row editors in the admin Storefront
 * "Pages" tab. An empty `sections` array renders just the title/intro.
 */
export type StorefrontPageSection =
  | { kind: 'paragraph'; heading?: string; body: string }
  | { kind: 'steps'; heading?: string; steps: string[] }
  | { kind: 'docs'; heading?: string; items: string[] };

export type StorefrontPage = {
  title: string;
  intro?: string;
  /** Optional R2 object key for a banner image at the top of the page. */
  heroImageKey?: string;
  sections: StorefrontPageSection[];
  /** Optional bottom call-to-action (e.g. "Register as a Merchant"). */
  ctaLabel?: string;
  ctaHref?: string;
};

/** A Customer-Care FAQ entry (Q&A). Optional `category` groups entries. */
export type FaqEntry = {
  question: string;
  answer: string;
  category?: string;
};

/** A "Follow Us" social link. Only entries with a non-empty `url` render. */
export type SocialLink = {
  platform: string;
  url: string;
  /** Optional R2 object key for the platform logo. */
  logoKey?: string;
};

/** A company testimonial shown on the About page. */
export type CompanyTestimonial = {
  author: string;
  /** Optional role / location line under the author's name. */
  role?: string;
  quote: string;
  /** Optional R2 object key for the author's photo. */
  avatarKey?: string;
};

/** A membership/package tier rendered by a landing's `packages` section. */
export type MembershipTier = {
  name: string;
  /** Display price, e.g. "₱2,500". */
  price: string;
  /** Point-value line, e.g. "25 PV". */
  pv?: string;
  /** Small qualifier under the price, e.g. "one-time". */
  oneTimeLabel?: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  /** The highlighted "Most Popular" tier. */
  featured?: boolean;
};

export type StorefrontConfig = {
  heroSlides: HeroSlide[];
  /** The two stacked cards beside the home hero — image-first promo tiles. */
  homeSideBanners: PromoBanner[];
  bottomBanners: PromoBanner[];
  campaignBanners: CampaignBanner[];
  collections: StorefrontCollection[];
  vouchers: StorefrontVoucher[];
  trustBadges: IconBadge[];
  assuranceBadges: IconBadge[];
  productVoucherBadges: ProductVoucherBadge[];
  shopHeader: { stats: IconStat[] };
  productStoreCard: { stats: StorefrontStat[] };
  header: {
    utilLinks: StorefrontLink[];
    searchSuggestions: string[];
  };
  footer: {
    brandName: string;
    brandBlurb: string;
    copyrightText: string;
    permitText: string;
    columns: FooterColumn[];
  };
  /**
   * Brand terms — the single source of truth for every place the storefront
   * names itself (mall badge, house store, wallet, legal entity). Every string
   * field is "empty = derive": a blank value is filled from `brandName` (or, for
   * `brandName` itself, from the footer brand / tenant name) by `resolveBrand`.
   * See `brand.ts`.
   */
  brand: {
    /** Public brand short name. '' → footer.brandName (if customized) → tenant name → 'Store'. */
    brandName: string;
    /**
     * Browser-tab / SEO title. '' → brandName. Kept separate from `brandName`
     * because the tab title is the one place a tenant wants its full name plus a
     * tagline ("Alpha Global Prestige - Adding Life by Sharing a Cup"), while
     * `brandName` must stay SHORT — it also renders the sidebar logo, the
     * storefront wordmark, the mall badge and the wallet label.
     */
    siteTitle: string;
    /** Legal entity for copyright / "Sold & shipped by". '' → brandName. */
    companyName: string;
    /** House "Official Store" display name. '' → `${brandName} Official Store`. */
    houseStoreName: string;
    /** Generic marketplace-seller display name. '' → `${brandName} Marketplace Seller`. */
    marketplaceSellerName: string;
    /** Product-card mall badge. '' → `${brandName.toUpperCase()} MALL`. */
    mallBadgeText: string;
    /** Borderline display policy: render the mall badge on product cards at all. */
    showMallBadge: boolean;
    /** Wallet display label. '' → `${brandName} Wallet`. */
    walletLabel: string;
    /**
     * What members BUY to activate a slot — a singular lowercase noun (e.g.
     * "course"). '' → the standard kit wording ("kit" / "activation kit" /
     * "membership kit"); a custom value replaces all three (see `resolveBrand`).
     */
    kitLabel: string;
  };
  copy: {
    cartVoucherHint: string;
    cartHouseVoucherHint: string;
    shippingGuarantee: string;
  };
  /** Informational pages reachable from the footer / header (CMS-managed). */
  pages: {
    about: StorefrontPage;
    becomeMember: StorefrontPage;
    sellOnOmni: StorefrontPage;
    merchantGuide: StorefrontPage;
  };
  /** Customer Care — a titled list of Q&A entries. */
  faq: {
    title: string;
    intro?: string;
    entries: FaqEntry[];
  };
  /** Follow Us — social links the tenant declares (only filled ones render). */
  social: SocialLink[];
  /** Company section shown on the About page (mission / vision / profile /
   * testimonials). Empty strings / empty list hide their respective blocks. */
  company: {
    mission: string;
    vision: string;
    /** Multi-paragraph plain text (split on blank lines when rendered). */
    profile: string;
    testimonials: CompanyTestimonial[];
  };
  /** Payment info page copy. The methods themselves are pulled live from
   * `ecom_payment_methods` (enabled only) — this block is just the heading/lede. */
  payment: {
    title: string;
    intro?: string;
  };
  /** Borderline policy: show the decorative (hash-based) "sold"/rating numbers on product cards. */
  showDecorativeSold: boolean;
  /**
   * Admin-curated product ids shown in the Official Store "Member-Exclusive
   * Deals" row, in the given order. Empty = the row is hidden. House products
   * only; resolved against the loaded catalog so per-buyer pricing is kept.
   */
  memberExclusiveDealProductIds: string[];
  /** Membership/package tiers for landing `packages` sections (empty by default). */
  membershipTiers: MembershipTier[];
  /**
   * Data-driven section/page layout (Layer 2). Absent → the Omni default; the
   * SPA renders pages from this. See `storefront-sections.ts`.
   */
  layout: StorefrontLayout;
  /**
   * Sparse text/image overrides for BESPOKE tenant sites (Layer 3 — the
   * hand-written `apps/member/src/tenants/<id>` pages). Keys are manifest
   * field paths (`page.section.field`, see `tenant-site-content/`); only
   * edited fields are stored. Absent for tenants without a bespoke site.
   */
  siteContent?: SiteContentOverlay;
};

/**
 * The stored shape behind the admin "Site Pages" editor. Flat path-keyed maps —
 * deliberately not a tree, so validation needs no recursion and unknown paths
 * are inert (the member-side resolver ignores anything its manifest doesn't
 * declare). `lists` entries replace the manifest default wholesale (same
 * semantics as `pickArray`); list-item image fields are named exactly
 * `imageKey` so `collectStorefrontImageKeys` can allowlist them generically.
 */
export type SiteContentOverlay = {
  /** `page.section.field` → replacement text. */
  text?: Record<string, string>;
  /** `page.section.field` → full replacement rows for an objectList/richParas/stringList field. */
  lists?: Record<string, Array<Record<string, string>>>;
  /** `page.section.field` → uploaded R2 object key. */
  images?: Record<string, string>;
};

const O_GRAD = 'linear-gradient(120deg,#FF6633,#EE4D2D 55%,#D4380D)';

/**
 * Defaults seeded with the exact values the storefront rendered while these were
 * hardcoded, so an un-configured tenant looks identical to before.
 */
export const STOREFRONT_DEFAULT: StorefrontConfig = {
  // `highlight` is the whole highlighted line now — the renderer used to prefix a
  // hardcoded "up to", which read as nonsense for most of these ("up to
  // Nationwide") and doubled up on the one that already said it. The two that
  // meant it now say so themselves.
  heroSlides: [
    {
      kicker: '6.6 Super Sale',
      title: 'Campaign Vouchers',
      highlight: 'up to ₱3,000 OFF',
      date: 'June 1 – 8',
    },
    {
      kicker: 'Member Week',
      title: 'Distributor Prices',
      highlight: 'up to 40% OFF',
      date: 'Members only',
    },
    {
      kicker: 'Free Shipping',
      title: 'No Minimum Spend',
      highlight: 'Nationwide',
      date: 'This week',
    },
    {
      kicker: 'Wallet Boost',
      title: 'Cashback Festival',
      highlight: 'up to ₱2,000 back',
      date: 'Limited slots',
    },
    {
      kicker: 'New Arrivals',
      title: 'Fresh Drops Daily',
      highlight: 'Shop New',
      date: 'Updated daily',
    },
  ],
  homeSideBanners: [
    { title: '', sub: '', gradient: 'linear-gradient(120deg,#7b2ff7,#b14bf4)' },
    { title: '', sub: '', gradient: O_GRAD },
  ],
  bottomBanners: [
    {
      title: 'Cash-in to Win',
      sub: 'Up to ₱2,000 in {wallet} credits',
      gradient: 'linear-gradient(120deg,#7b2ff7,#b14bf4)',
    },
    {
      title: 'Monthly Installment Plans',
      sub: '0% interest with accredited partners',
      gradient: O_GRAD,
    },
    {
      title: 'Save More with {wallet}',
      sub: 'Instant cashback on every checkout',
      gradient: 'linear-gradient(120deg,#0b8f5a,#37c97e)',
    },
  ],
  campaignBanners: [
    {
      title: 'Member-Exclusive Prices',
      sub: 'Sign in as a member to unlock distributor pricing on every item.',
      ctaLabel: 'Become a member →',
      ctaHref: '/signup',
      gradient: 'linear-gradient(120deg,#7b2ff7,#b14bf4)',
    },
    {
      title: '6.6 Flagship Sale',
      sub: 'Up to 40% off bestsellers + free shipping over ₱500. June 1–8.',
      ctaLabel: 'Shop the sale →',
      ctaHref: '/official',
      gradient: O_GRAD,
    },
  ],
  collections: [
    { name: 'Wellness', items: '84 items' },
    { name: 'Beauty & Glow', items: '62 items' },
    { name: 'Coffee & Drinks', items: '38 items' },
    { name: 'Supplements', items: '71 items' },
    { name: 'Activation Kits', items: '9 items' },
    { name: 'Bundles & Gifts', items: '24 items' },
  ],
  vouchers: [
    { big: '₱100', sm: 'OFF', terms: 'Min. spend ₱1,000', validity: 'Valid till 06.30' },
    { big: '₱50', sm: 'OFF', terms: 'Min. spend ₱500', validity: 'Valid till 06.30' },
    { big: '15%', sm: 'OFF', terms: 'Capped at ₱300', validity: 'New buyers only' },
    { big: '₱200', sm: 'OFF', terms: 'Min. spend ₱2,500', validity: 'Activation kits' },
  ],
  trustBadges: [
    { label: '100% Authentic', icon: 'shield' },
    { label: '15-Day Free Returns', icon: 'rotate' },
    { label: 'Nationwide Shipping', icon: 'truck' },
    { label: 'Earn PV + Wallet Cashback', icon: 'coins' },
    { label: 'Secure Checkout', icon: 'lock' },
  ],
  assuranceBadges: [
    { label: '100% Authentic', icon: 'shield' },
    { label: '15-Day Free Returns', icon: 'rotate' },
    { label: 'Free Shipping over ₱500', icon: 'truck' },
    { label: 'Cash on Delivery', icon: 'banknote' },
  ],
  productVoucherBadges: [
    { line1: '₱50 OFF', line2: 'Voucher' },
    { line1: 'Free', line2: 'Shipping' },
  ],
  shopHeader: {
    stats: [
      { icon: 'box', value: '142', label: 'Products', live: 'productCount' },
      { icon: 'star', value: '4.9 (12.3k)', label: 'Rating' },
      { icon: 'users', value: '128.4k', label: 'Followers' },
      { icon: 'chat', value: '98% (1 Hr)', label: 'Chat Response' },
      { icon: 'user', value: '38', label: 'Following' },
      { icon: 'clock', value: '3 years ago', label: 'Joined' },
    ],
  },
  productStoreCard: {
    stats: [
      { value: '98.6%', label: 'Positive Ratings' },
      { value: '142', label: 'Products', live: 'productCount' },
      { value: '99%', label: 'Ship On Time' },
      { value: '1 Hour', label: 'Chat Response' },
    ],
  },
  header: {
    utilLinks: [{ label: 'How to Sell on {BRAND}', href: '/sell-on-omni' }],
    searchSuggestions: [
      'Activation Kit',
      'Wellness Coffee',
      'Glow Serum',
      'Coffee',
      'Skincare',
      'Supplements',
    ],
  },
  footer: {
    brandName: '{brand}',
    brandBlurb:
      'The member storefront for {company}. Authentic products, transparent pricing, rewards to your wallet.',
    copyrightText: '© {year} {company}',
    permitText: 'Per DTI Fair Trade Permit No. FTEB-000000 Series of 2026',
    columns: [
      {
        heading: 'Customer Service',
        links: [
          { label: 'Customer Care', href: '/faq' },
          { label: 'Track Order', href: '/track' },
          { label: 'Send Feedback', href: '/feedback' },
          { label: 'Shipping Info', href: '/faq' },
        ],
      },
      {
        heading: '{brand}',
        links: [
          { label: 'About Us', href: '/about' },
          { label: 'Become a Member', href: '/become-member' },
          { label: 'Sell on {brand}', href: '/sell-on-omni' },
          { label: 'Become a Merchant', href: '/become-merchant' },
        ],
      },
      { heading: 'Payment', links: [{ label: 'Payment Methods', href: '/payment' }] },
    ],
  },
  brand: {
    brandName: '',
    siteTitle: '',
    companyName: '',
    houseStoreName: '',
    marketplaceSellerName: '',
    mallBadgeText: '',
    showMallBadge: true,
    walletLabel: '',
    kitLabel: '',
  },
  copy: {
    cartVoucherHint: 'Free shipping voucher available',
    cartHouseVoucherHint: 'Get ₱50 off when you spend ₱1,000',
    shippingGuarantee: 'Get up to ₱50 if order arrives late',
  },
  pages: {
    about: {
      title: 'About Us',
      intro:
        'Authentic products, transparent pricing, and rewards that go straight to your wallet.',
      sections: [
        {
          kind: 'paragraph',
          heading: 'Who we are',
          body: 'We are a member-driven marketplace built on quality products and a fair, transparent rewards system. Every purchase supports a community of members earning together.',
        },
      ],
    },
    becomeMember: {
      title: 'Become a Member',
      intro: 'Unlock distributor pricing, earn rewards on every order, and build your own network.',
      sections: [
        {
          kind: 'steps',
          heading: 'How to join',
          steps: [
            'Sign up for a free account.',
            'Purchase an Activation Kit to unlock member pricing.',
            'Redeem your Kit code to activate your slot.',
            'Start shopping at member prices and earning rewards.',
          ],
        },
      ],
      ctaLabel: 'Sign Up',
      ctaHref: '/signup',
    },
    sellOnOmni: {
      title: 'Sell on {brand}',
      intro: 'Reach our members and shoppers by listing your products on the marketplace.',
      sections: [
        {
          kind: 'paragraph',
          body: 'Becoming a merchant lets you list and sell your own products to our community. Apply, get verified, and start selling.',
        },
      ],
      ctaLabel: 'Become a Merchant',
      ctaHref: '/become-merchant',
    },
    merchantGuide: {
      title: 'Become a Merchant',
      intro: 'A step-by-step guide to selling on {brand} as a verified merchant reseller.',
      sections: [
        {
          kind: 'steps',
          heading: 'How to become a merchant',
          steps: [
            'Submit your merchant application with your business details.',
            'Upload the required documents for verification.',
            'Wait for admin review and approval.',
            'Once approved, list your products and start selling.',
          ],
        },
        {
          kind: 'docs',
          heading: 'Documents needed',
          items: [
            'Valid government-issued ID',
            'Business permit or DTI/SEC registration',
            'BIR Certificate of Registration',
            'Proof of bank account for payouts',
          ],
        },
        {
          kind: 'paragraph',
          heading: 'Terms & Agreement',
          body: 'By registering as a merchant reseller you agree to our marketplace policies, applicable platform fees, and accurate product representation. Full terms are provided during registration and must be accepted before your store goes live.',
        },
      ],
      ctaLabel: 'Register as a Merchant',
      ctaHref: '/become-merchant',
    },
  },
  faq: {
    title: 'Customer Care',
    intro: 'Answers to the questions we hear most often.',
    entries: [
      {
        question: 'How do I track my order?',
        answer:
          'Once your order ships, you can track it from your order details or the Track Order page using your courier’s tracking link.',
        category: 'Orders',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept the payment methods listed on our Payment page. Available options may vary.',
        category: 'Payment',
      },
      {
        question: 'How do I become a member?',
        answer:
          'Sign up for a free account, purchase an Activation Kit, and redeem your Kit code to activate your slot.',
        category: 'Membership',
      },
    ],
  },
  social: [],
  company: {
    mission:
      'To make authentic, quality products accessible to every member at fair, transparent prices — while rewarding the community that grows with us.',
    vision:
      'A trusted marketplace where members build lasting income through honest commerce and shared success.',
    profile:
      'We are a member-driven marketplace built on quality products and a fair, transparent rewards system. Every purchase supports a community of members earning together.\n\nFrom wellness and beauty to everyday essentials, we partner with vetted suppliers and reputable merchants to bring our members genuine products and dependable service nationwide.',
    testimonials: [],
  },
  payment: {
    title: 'Payment Methods',
    intro: 'These are the payment options currently available at checkout.',
  },
  showDecorativeSold: true,
  memberExclusiveDealProductIds: [],
  membershipTiers: [],
  layout: LAYOUT_DEFAULT_OMNI,
};

/**
 * Deep-merge a stored storefront blob against `STOREFRONT_DEFAULT`. Arrays are
 * replaced wholesale when present (the admin owns the full list), but a missing
 * array/sub-object falls back to its default — so a blob written before a new
 * sub-block existed still resolves to a complete config. Top-level object
 * sub-blocks (`shopHeader`, `header`, `footer`, `copy`, …) are merged one
 * level deep.
 */
export function resolveStorefront(
  raw: Partial<StorefrontConfig> | null | undefined,
): StorefrontConfig {
  if (!raw || typeof raw !== 'object') return clone(STOREFRONT_DEFAULT);
  const d = STOREFRONT_DEFAULT;
  return {
    heroSlides: pickArray(raw.heroSlides, d.heroSlides),
    homeSideBanners: pickArray(raw.homeSideBanners, d.homeSideBanners),
    bottomBanners: pickArray(raw.bottomBanners, d.bottomBanners),
    campaignBanners: pickArray(raw.campaignBanners, d.campaignBanners),
    collections: pickArray(raw.collections, d.collections),
    vouchers: pickArray(raw.vouchers, d.vouchers),
    trustBadges: pickArray(raw.trustBadges, d.trustBadges),
    assuranceBadges: pickArray(raw.assuranceBadges, d.assuranceBadges),
    productVoucherBadges: pickArray(raw.productVoucherBadges, d.productVoucherBadges),
    shopHeader: { stats: pickArray(raw.shopHeader?.stats, d.shopHeader.stats) },
    productStoreCard: { stats: pickArray(raw.productStoreCard?.stats, d.productStoreCard.stats) },
    header: {
      utilLinks: pickArray(raw.header?.utilLinks, d.header.utilLinks),
      searchSuggestions: pickArray(raw.header?.searchSuggestions, d.header.searchSuggestions),
    },
    footer: {
      brandName: pickStr(raw.footer?.brandName, d.footer.brandName),
      brandBlurb: pickStr(raw.footer?.brandBlurb, d.footer.brandBlurb),
      copyrightText: pickStr(raw.footer?.copyrightText, d.footer.copyrightText),
      permitText: pickStr(raw.footer?.permitText, d.footer.permitText),
      columns: pickArray(raw.footer?.columns, d.footer.columns),
    },
    brand: {
      brandName: pickStr(raw.brand?.brandName, d.brand.brandName),
      siteTitle: pickStr(raw.brand?.siteTitle, d.brand.siteTitle),
      companyName: pickStr(raw.brand?.companyName, d.brand.companyName),
      houseStoreName: pickStr(raw.brand?.houseStoreName, d.brand.houseStoreName),
      marketplaceSellerName: pickStr(
        raw.brand?.marketplaceSellerName,
        d.brand.marketplaceSellerName,
      ),
      mallBadgeText: pickStr(raw.brand?.mallBadgeText, d.brand.mallBadgeText),
      showMallBadge:
        typeof raw.brand?.showMallBadge === 'boolean'
          ? raw.brand.showMallBadge
          : d.brand.showMallBadge,
      walletLabel: pickStr(raw.brand?.walletLabel, d.brand.walletLabel),
      kitLabel: pickStr(raw.brand?.kitLabel, d.brand.kitLabel),
    },
    copy: {
      cartVoucherHint: pickStr(raw.copy?.cartVoucherHint, d.copy.cartVoucherHint),
      cartHouseVoucherHint: pickStr(raw.copy?.cartHouseVoucherHint, d.copy.cartHouseVoucherHint),
      shippingGuarantee: pickStr(raw.copy?.shippingGuarantee, d.copy.shippingGuarantee),
    },
    pages: {
      about: pickPage(raw.pages?.about, d.pages.about),
      becomeMember: pickPage(raw.pages?.becomeMember, d.pages.becomeMember),
      sellOnOmni: pickPage(raw.pages?.sellOnOmni, d.pages.sellOnOmni),
      merchantGuide: pickPage(raw.pages?.merchantGuide, d.pages.merchantGuide),
    },
    faq: {
      title: pickStr(raw.faq?.title, d.faq.title),
      intro: pickStr(raw.faq?.intro, d.faq.intro ?? ''),
      entries: pickArray(raw.faq?.entries, d.faq.entries),
    },
    social: pickArray(raw.social, d.social),
    company: {
      mission: pickStr(raw.company?.mission, d.company.mission),
      vision: pickStr(raw.company?.vision, d.company.vision),
      profile: pickStr(raw.company?.profile, d.company.profile),
      testimonials: pickArray(raw.company?.testimonials, d.company.testimonials),
    },
    payment: {
      title: pickStr(raw.payment?.title, d.payment.title),
      intro: pickStr(raw.payment?.intro, d.payment.intro ?? ''),
    },
    showDecorativeSold:
      typeof raw.showDecorativeSold === 'boolean' ? raw.showDecorativeSold : d.showDecorativeSold,
    memberExclusiveDealProductIds: pickArray(
      raw.memberExclusiveDealProductIds,
      d.memberExclusiveDealProductIds,
    ),
    membershipTiers: pickArray(raw.membershipTiers, d.membershipTiers),
    layout: resolveLayout(raw.layout),
    // Passthrough, no default merge — the manifest carries the defaults. This
    // line is what keeps the overlay alive through the admin editors' full-block
    // saves (they seed drafts from resolveStorefront and send the whole object).
    siteContent:
      raw.siteContent && typeof raw.siteContent === 'object' ? clone(raw.siteContent) : undefined,
  };
}

/**
 * Every R2 object key referenced by the config (slide/banner/collection/campaign
 * images). The public asset route serves a key ONLY if it's in this set, so the
 * route can't be used as an open presign oracle for arbitrary keys.
 */
export function collectStorefrontImageKeys(cfg: StorefrontConfig): Set<string> {
  const keys = new Set<string>();
  const add = (k?: string) => {
    if (k && !/^https?:\/\//i.test(k)) keys.add(k);
  };
  cfg.heroSlides.forEach((s) => add(s.imageKey));
  cfg.homeSideBanners.forEach((b) => add(b.imageKey));
  cfg.bottomBanners.forEach((b) => add(b.imageKey));
  cfg.campaignBanners.forEach((b) => add(b.imageKey));
  cfg.collections.forEach((c) => add(c.imageKey));
  cfg.social.forEach((s) => add(s.logoKey));
  cfg.company.testimonials.forEach((t) => add(t.avatarKey));
  Object.values(cfg.pages).forEach((p) => add(p.heroImageKey));
  const sc = cfg.siteContent;
  if (sc) {
    // '/'-prefixed values are baked public paths (manifest defaults copied into
    // an overlay row), not R2 keys — keep them out of the serve allowlist.
    const addKey = (k?: string) => {
      if (k && !k.startsWith('/')) add(k);
    };
    Object.values(sc.images ?? {}).forEach(addKey);
    Object.values(sc.lists ?? {}).forEach((items) => items.forEach((it) => addKey(it.imageKey)));
  }
  return keys;
}

function pickArray<T>(v: T[] | undefined, fallback: T[]): T[] {
  return Array.isArray(v) ? v : clone(fallback);
}

function pickStr(v: string | undefined, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/** Merge one informational page against its default — strings fall back per
 * field, `sections` is replaced wholesale when present (the admin owns the list). */
function pickPage(
  v: Partial<StorefrontPage> | undefined,
  fallback: StorefrontPage,
): StorefrontPage {
  if (!v || typeof v !== 'object') return clone(fallback);
  return {
    title: pickStr(v.title, fallback.title),
    intro: pickStr(v.intro, fallback.intro ?? ''),
    heroImageKey: pickStr(v.heroImageKey, fallback.heroImageKey ?? '') || undefined,
    sections: pickArray(v.sections, fallback.sections),
    ctaLabel: pickStr(v.ctaLabel, fallback.ctaLabel ?? '') || undefined,
    ctaHref: pickStr(v.ctaHref, fallback.ctaHref ?? '') || undefined,
  };
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}
